import React, { useState, useEffect, useRef } from 'react';
import { match } from 'pinyin-pro';
import {
  X, Search, Book, Palette, Star, Moon, Coffee, Trophy, Music, Heart, Sun, Zap, ThumbsUp, ThumbsDown, Users, History, Cat, Clock, Plus, Minus,
  Carrot, Apple, Pizza, Utensils, Cake, IceCream, Grape, Banana, Cherry, Croissant, Sandwich, Cookie, Drumstick, GlassWater, Popcorn,
  Ruler, Notebook, Paperclip, Scissors, Highlighter, Folder, Calculator, Pen, Pin,
  Gamepad2, Headphones, Tv, Ticket, Dices, Ghost, Skull, Puzzle,
  Crown, Medal, Award, Sparkles, GraduationCap, Gift, Package,
  Smile, Lightbulb, Bell, Flag, Calendar, Briefcase, Check, Key, Lock, Unlock,
  Dumbbell, Bike, Activity, Target, Map, Compass,
  Snowflake, Flame, Droplets, Flower, Leaf, TreeDeciduous, Mountain, Cloud,
  Dog, Rabbit, Bird, Snail, Turtle, Fish,
  Rocket, Plane, Car, Bus, Truck, Ship,
  CheckCircle2, RotateCcw, Download, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../lib/api';
import { normalizeLanguage } from '../i18n';
import { useClassStore } from '../store/useClassStore';
import {
  defaultPetLifeStateConfig,
  derivePetLifeInfo,
  getPetImage,
  getPetLifeStateStyle,
  getPetLifeStateText,
  getPetName,
  getPreviewImage,
  parsePetLifeStateConfig,
  type PetLifeState,
  type PetLifeStateConfig,
} from '../lib/petUtils';
import { usePetStore } from '../store/usePetStore';
import CosImage from './CosImage';

interface PointItem {
  id: number;
  name: string;
  points: number;
  icon: string;
}

interface Student {
  id: number;
  name: string;
  currentPoints: number;
  availablePoints?: number;
  petType: string | null;
  nickname?: string | null;
  unlockedPets?: string[];
  lastFeedTime?: string | null;
  lifeState?: PetLifeState | null;
  sleepTime?: string | null;
}

interface PointHistory {
  id: number;
  studentId: number;
  amount: number;
  reason: string;
  createdAt: string;
  revertedFromId?: number | null;
  revertedAt?: string | null;
}

interface CheckinTask {
  id: number;
  name: string;
  points: number;
  icon: string | null;
  enabled: boolean;
  order: number;
}

interface CheckinRecordItem {
  id: number;
  taskId: number;
  studentId: number;
  pointsAwarded: number;
}

interface StudentActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  student?: Student | null;
  students?: Student[];
  onUpdate: (points: number) => void;
  onChangePet?: (student: Student) => void;
}

// ---------- Calendar helpers ----------
function formatDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function normalizeDateString(value: string | Date | null | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  return formatDateKey(value);
}

// Builds a 6-row × 7-col grid (Mon → Sun) of date cells covering the given month.
function buildCalendarCells(monthDate: Date): { date: Date; inMonth: boolean }[] {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  // (getDay()+6)%7 → Mon=0, Sun=6
  const startCol = (firstOfMonth.getDay() + 6) % 7;
  const cells: { date: Date; inMonth: boolean }[] = [];
  for (let i = startCol; i > 0; i -= 1) {
    cells.push({ date: new Date(year, month, 1 - i), inMonth: false });
  }
  const lastDay = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= lastDay; d += 1) {
    cells.push({ date: new Date(year, month, d), inMonth: true });
  }
  while (cells.length < 42) {
    const last = cells[cells.length - 1].date;
    cells.push({
      date: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1),
      inMonth: false,
    });
  }
  return cells;
}

const iconMap: Record<string, React.ElementType> = {
  // Food
  Carrot, Apple, Pizza, Utensils, Cake, IceCream, Grape, Banana, Cherry, Croissant, Sandwich, Cookie, Drumstick, GlassWater, Popcorn, Coffee,
  // Stationery
  Book, Ruler, Notebook, Paperclip, Scissors, Highlighter, Folder, Calculator, Pen, Pin, Palette,
  // Entertainment
  Gamepad2, Headphones, Tv, Ticket, Dices, Ghost, Skull, Puzzle, Music,
  // Privilege/Awards
  Crown, Medal, Award, Sparkles, GraduationCap, Gift, Package, Star, Trophy, Heart,
  // Sports/Transport
  Dumbbell, Bike, Activity, Target, Rocket, Plane, Car, Bus, Truck, Ship, Map, Compass,
  // Nature/Animals
  Sun, Moon, Cloud, Snowflake, Flame, Droplets, Flower, Leaf, TreeDeciduous, Mountain,
  Cat, Dog, Rabbit, Bird, Snail, Turtle, Fish,
  // Misc
  Smile, Lightbulb, Bell, Flag, Calendar, Briefcase, Check, Key, Lock, Unlock, Zap,
  // UI/Other
  ThumbsUp, ThumbsDown, Users, History, Clock, Search, X
};

export default function StudentActionModal({
  isOpen,
  onClose,
  student,
  students,
  onUpdate,
  onChangePet,
}: StudentActionModalProps) {
  const { currentClass } = useClassStore();
  const { petConfig } = usePetStore();
  const { t, i18n } = useTranslation();
  const [pointItems, setPointItems] = useState<PointItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'action' | 'history' | 'pets' | 'calendar'>('action');
  const [history, setHistory] = useState<PointHistory[]>([]);
  const [growthConfig, setGrowthConfig] = useState<Record<string, number>>({});
  const [undoTarget, setUndoTarget] = useState<PointHistory | null>(null);
  const [undoSubmitting, setUndoSubmitting] = useState(false);
  const [petLifeConfig, setPetLifeConfig] = useState<PetLifeStateConfig>(defaultPetLifeStateConfig);
  const activeLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const isSubmittingRef = React.useRef(false);

  const [localStudent, setLocalStudent] = useState<Student | null>(null);
  const [actionSubTab, setActionSubTab] = useState<'checkin' | 'behavior'>('checkin');
  const [behaviorFilter, setBehaviorFilter] = useState<'all' | 'positive' | 'negative'>('all');
  const [checkinTasks, setCheckinTasks] = useState<CheckinTask[]>([]);
  const [checkinRecords, setCheckinRecords] = useState<CheckinRecordItem[]>([]);
  const [checkinOperatingId, setCheckinOperatingId] = useState<number | null>(null);
  // Calendar tab state
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [calendarRecords, setCalendarRecords] = useState<Array<{ id: number; date: string; taskId: number; pointsAwarded: number; task?: { id: number; name: string } | null }>>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarSelectedDay, setCalendarSelectedDay] = useState<string | null>(null);
  const [customScoreOpen, setCustomScoreOpen] = useState(false);
  const [customScoreType, setCustomScoreType] = useState<'positive' | 'negative'>('positive');
  const [customScoreValue, setCustomScoreValue] = useState('');
  const [customScoreReason, setCustomScoreReason] = useState('');
  const customScoreInputRef = useRef<HTMLInputElement>(null);
  // const shouldAutoFocusSearch =
  //   typeof window !== 'undefined' &&
  //   typeof window.matchMedia === 'function' &&
  //   window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  const defaultGrowthConfig: Record<string, number> = {
    '0': 0,
    '1': 5,
    '2': 10,
    '3': 20,
    '4': 30,
    '5': 45,
    '6': 60,
    '7': 75,
    '8': 90,
    '9': 100,
  };
  const parseGrowthConfig = (value: unknown): Record<string, number> | null => {
    if (typeof value !== 'string') return null;
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        const record: Record<string, number> = {};
        parsed.forEach((v, idx) => {
          const n = Number(v);
          if (Number.isFinite(n)) record[idx.toString()] = n;
        });
        if (Object.keys(record).length === 0) return null;
        record['0'] = 0;
        return record;
      }
      if (parsed && typeof parsed === 'object') {
        const record: Record<string, number> = {};
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
          const n = Number(v);
          if (Number.isFinite(n)) record[k] = n;
        }
        if (Object.keys(record).length === 0) return null;
        if (!('0' in record)) record['0'] = 0;
        return record;
      }
      return null;
    } catch {
      return null;
    }
  };
  
  // Determine target students (single or batch)
  const targetStudents = students && students.length > 0 ? students : (student ? [student] : []);
  const isBatchMode = targetStudents.length > 1;
  const currentStudent = !isBatchMode ? (localStudent || targetStudents[0]) : null;

  // Group history by date
  const groupedHistory = React.useMemo(() => {
    const groups: Record<string, PointHistory[]> = {};
    history.forEach(record => {
      // Skip both halves of a revert pair so the history list mirrors the
      // user's mental model: a check-in toggle dance shouldn't leave
      // visible noise behind.
      // - revertedAt != null  → original entry that has been undone
      // - revertedFromId != null → the undo entry itself
      if (record.revertedAt || record.revertedFromId) return;
      const date = new Date(record.createdAt).toLocaleDateString(activeLanguage, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      if (!groups[date]) groups[date] = [];
      groups[date].push(record);
    });
    return groups;
  }, [history, activeLanguage]);

  useEffect(() => {
    if (currentClass) {
      fetchGrowthConfig();
      fetchPetLifeConfig();
    }
  }, [currentClass]);

  useEffect(() => {
    if (isOpen && currentClass) {
      fetchPointItems();
      fetchCheckinStatus();
    }
  }, [isOpen, currentClass?.id]);

  useEffect(() => {
    if (isOpen && currentClass) {
      setSearchTerm('');
      setActiveTab('action');
      setActionSubTab(isBatchMode ? 'behavior' : 'checkin');
      setBehaviorFilter('all');
      // Reset calendar to current month each time the modal opens.
      const now = new Date();
      setCalendarMonth(new Date(now.getFullYear(), now.getMonth(), 1));
      setCalendarRecords([]);
      if (!isBatchMode && targetStudents[0]) {
        setLocalStudent(targetStudents[0]);
        fetchHistory(targetStudents[0].id);
      } else {
        setLocalStudent(null);
      }
    }
  }, [isOpen, isBatchMode, targetStudents[0]?.id]);

  // Load check-in calendar whenever the calendar tab is active for a student
  // or the user navigates to a different month.
  useEffect(() => {
    if (isOpen && !isBatchMode && activeTab === 'calendar' && currentStudent && currentClass) {
      fetchCalendarRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, currentStudent?.id, calendarMonth, currentClass?.id]);

  const fetchGrowthConfig = async () => {
    if (!currentClass) return;
    try {
      const response = await api.get(`/settings/key/growth_config?classId=${currentClass.id}`);
      const classConfig = parseGrowthConfig(response.data?.value);
      if (classConfig) {
        setGrowthConfig(classConfig);
        return;
      }
      setGrowthConfig(defaultGrowthConfig);
    } catch (error) {
      console.error('Failed to fetch growth config', error);
      setGrowthConfig(defaultGrowthConfig);
    }
  };

  const fetchPointItems = async () => {
    if (!currentClass) return;
    try {
      const response = await api.get(`/point-items?classId=${currentClass.id}`);
      setPointItems(response.data);
    } catch (error) {
      console.error('Failed to fetch point items:', error);
    }
  };

  const fetchCheckinStatus = async () => {
    if (!currentClass) return;
    try {
      const response = await api.get(`/checkin/today?classId=${currentClass.id}`);
      setCheckinTasks(response.data?.tasks || []);
      setCheckinRecords(response.data?.records || []);
    } catch (error) {
      console.error('Failed to fetch check-in status:', error);
    }
  };

  const fetchCalendarRecords = async () => {
    if (!currentClass || !currentStudent) return;
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const startDate = formatDateKey(new Date(year, month, 1));
    const endDate = formatDateKey(new Date(year, month + 1, 0));
    setCalendarLoading(true);
    try {
      const response = await api.get('/checkin/history', {
        params: {
          classId: currentClass.id,
          studentId: currentStudent.id,
          startDate,
          endDate,
        },
      });
      setCalendarRecords(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch calendar records:', error);
      setCalendarRecords([]);
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleToggleCheckin = async (taskId: number) => {
    if (!currentStudent || checkinOperatingId !== null) return;
    const task = checkinTasks.find(t => t.id === taskId);
    if (!task) return;
    const studentId = currentStudent.id;
    const completed = checkinRecords.some(r => r.taskId === taskId && r.studentId === studentId);
    const pointsDelta = completed ? -task.points : task.points;
    setCheckinOperatingId(taskId);
    try {
      if (completed) {
        await api.post('/checkin/uncomplete', { taskId, studentId });
        setCheckinRecords(prev => prev.filter(r => !(r.taskId === taskId && r.studentId === studentId)));
      } else {
        const res = await api.post('/checkin/complete', { taskId, studentId });
        if (res.data?.record) {
          setCheckinRecords(prev => [...prev, res.data.record]);
        }
      }
      try {
        const studentRes = await api.get(`/students/${studentId}`);
        if (studentRes.data) {
          setLocalStudent(studentRes.data);
        }
      } catch (err) {
        console.error('Failed to fetch updated student info', err);
      }
      onUpdate(pointsDelta);
      fetchHistory(studentId);
    } catch (error) {
      console.error('Failed to toggle check-in:', error);
    } finally {
      setCheckinOperatingId(null);
    }
  };

  const fetchPetLifeConfig = async () => {
    if (!currentClass) return;

    if (currentClass.isAttenuationEnabled === false) {
      setPetLifeConfig(defaultPetLifeStateConfig);
      return;
    }

    try {
      const response = await api.get(`/settings/key/pet_life_state_config?classId=${currentClass.id}`);
      setPetLifeConfig(parsePetLifeStateConfig(response.data?.value));
    } catch (error) {
      console.error('Failed to fetch pet life state config:', error);
      setPetLifeConfig(defaultPetLifeStateConfig);
    }
  };

  const fetchHistory = async (studentId: number) => {
    try {
      const response = await api.get(`/students/${studentId}/history`, {
        params: { page: 1, limit: 200 },
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const handleExportHistory = () => {
    if (!currentStudent) return;

    const fetchAll = async () => {
      const limit = 2000;
      const maxPages = 5;
      const all: PointHistory[] = [];
      for (let page = 1; page <= maxPages; page += 1) {
        const res = await api.get(`/students/${currentStudent.id}/history`, {
          params: { page, limit },
        });
        const items = (res.data ?? []) as PointHistory[];
        all.push(...items);
        if (items.length < limit) break;
      }
      return all;
    };

    void (async () => {
      const items = await fetchAll().catch(() => []);
      if (items.length === 0) return;

    // CSV Headers
    const headers = [
      t('growthRecords.csv.time'),      // Time
      t('growthRecords.csv.action'),    // Reason
      t('growthRecords.csv.change'),    // Points
      t('studentAction.reverted')       // Status
    ];

    // CSV Rows
    const rows = items.map(record => {
      const date = new Date(record.createdAt).toLocaleString(activeLanguage);
      const isPrestige = record.amount === 0;
      let reason = record.reason || t('studentAction.unknownAction');
      if (isPrestige) reason = t('common.prestigeReason');
      
      const amount = record.amount > 0 ? `+${record.amount}` : `${record.amount}`;
      const revertedStatus = record.revertedAt ? t('studentAction.reverted') : '';

      const escape = (str: string) => `"${str.replace(/"/g, '""')}"`;

      return [
        escape(date),
        escape(reason),
        escape(amount),
        escape(revertedStatus)
      ].join(',');
    });

    const csvContent = "\uFEFF" + [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `History_${currentStudent?.name || 'Student'}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    })();
  };

  const handleUpdatePoints = async (points: number, reason: string) => {
    if (targetStudents.length === 0 || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setLoading(true);

    try {
      // Use batch endpoint for optimized updates
      const studentIds = targetStudents.map(s => s.id);
      await api.post('/students/batch/points', { studentIds, points, reason });
      
      // Update local student state if in single mode
      if (!isBatchMode && currentStudent) {
        // Fetch fresh student data to update display (points, level, pet image)
        try {
          const studentRes = await api.get(`/students/${currentStudent.id}`);
          if (studentRes.data) {
             setLocalStudent(studentRes.data);
          }
        } catch (err) {
          console.error('Failed to fetch updated student info', err);
        }
      }

      onUpdate(points); // Notify parent to refresh list
      
      // If single mode, refresh history
      if (currentStudent) {
        fetchHistory(currentStudent.id);
      }
    } catch (error) {
      console.error('Failed to update student points:', error);
    } finally {
      // Add a small delay to prevent double clicks (throttle)
      setTimeout(() => {
        setLoading(false);
        isSubmittingRef.current = false;
      }, 500);
    }
  };

  const submitUndo = async () => {
    if (!undoTarget || !currentStudent) return;
    setUndoSubmitting(true);
    try {
      await api.post(`/students/history/${undoTarget.id}/revert`);
      try {
        const studentRes = await api.get(`/students/${currentStudent.id}`);
        if (studentRes.data) {
          setLocalStudent(studentRes.data);
        }
      } catch (err) {
        console.error('Failed to fetch updated student info', err);
      }
      onUpdate(-undoTarget.amount);
      await fetchHistory(currentStudent.id);
      setUndoTarget(null);
    } catch (error) {
      console.error('Undo failed', error);
      alert(t('studentAction.undoFailed'));
    } finally {
      setUndoSubmitting(false);
    }
  };

  if (!isOpen || targetStudents.length === 0) return null;

  const filteredItems = pointItems.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    match(item.name, searchTerm)
  );

  const positiveItems = filteredItems.filter(item => item.points > 0);
  const negativeItems = filteredItems.filter(item => item.points < 0);

  const calculateLevelInfo = (currentPoints: number) => {
    const config = Object.keys(growthConfig).length > 0 ? growthConfig : defaultGrowthConfig;
    let level = 1;
    const maxLevel = 10;
    
    // Find current level based on cumulative points
    for (let i = 1; i <= maxLevel; i++) {
      const requiredPoints = config[i.toString()];
      if (currentPoints >= requiredPoints) {
        level = i + 1;
      } else {
        break;
      }
    }
    
    // Cap level at maxLevel
    if (level > maxLevel) level = maxLevel;

    // Calculate progress
    if (level === maxLevel) {
      return { level, progress: 100 };
    }

    const currentLevelThreshold = config[(level - 1).toString()] || 0;
    const nextLevelThreshold = config[level.toString()] || 100;
    
    // Calculate progress percentage for current level
    const pointsInCurrentLevel = currentPoints - currentLevelThreshold;
    const pointsSpan = nextLevelThreshold - currentLevelThreshold;
    const progress = Math.min(100, Math.max(0, (pointsInCurrentLevel / pointsSpan) * 100));

    return { level, progress };
  };
  
  const { level, progress } = currentStudent ? calculateLevelInfo(currentStudent.currentPoints) : { level: 1, progress: 0 };
  const forceSleeping = currentClass?.isAttenuationEnabled !== false && (currentStudent?.currentPoints ?? 0) < 0;
  const petLifeInfo = currentStudent
    ? currentClass?.isAttenuationEnabled === false
      ? derivePetLifeInfo(undefined, petLifeConfig, 'normal')
      : forceSleeping
        ? derivePetLifeInfo(undefined, petLifeConfig, 'dead')
        : derivePetLifeInfo(currentStudent.lastFeedTime, petLifeConfig, currentStudent.lifeState ?? undefined)
    : null;
  const petLifeStyle = petLifeInfo ? getPetLifeStateStyle(petLifeInfo.lifeState) : null;
  const hidePetStatus = !!currentStudent?.petType && (currentStudent?.currentPoints ?? 0) <= 0 && !forceSleeping;
 
  const handleOpenCustomScore = (type: 'positive' | 'negative') => {
    setCustomScoreType(type);
    setCustomScoreValue('');
    setCustomScoreReason('');
    setCustomScoreOpen(true);
    setTimeout(() => customScoreInputRef.current?.focus(), 100);
  };

  const handleCustomScoreSubmit = () => {
    const points = parseInt(customScoreValue, 10);
    if (!points || points <= 0 || points > 9999) return;
    const actualPoints = customScoreType === 'positive' ? points : -points;
    const reason = customScoreReason.trim() || (customScoreType === 'positive' ? t('studentAction.custom.defaultPositive') : t('studentAction.custom.defaultNegative'));
    handleUpdatePoints(actualPoints, reason);
    setCustomScoreOpen(false);
  };

  const renderCustomCard = (type: 'positive' | 'negative') => {
    const isPositive = type === 'positive';
    return (
      <button
        key={`custom-${type}`}
        onClick={() => handleOpenCustomScore(type)}
        disabled={loading}
        className={`
          flex flex-col items-center justify-center p-2 md:p-3 rounded-2xl border-2 border-dashed
          transition-all duration-200 ease-out active:scale-[0.96]
          ${isPositive
            ? 'border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50/60'
            : 'border-rose-200 hover:border-rose-400 hover:bg-rose-50/60'
          }
          hover:shadow-clay hover:-translate-y-0.5
        `}
      >
        <div className={`
          w-9 h-9 md:w-11 md:h-11 rounded-2xl flex items-center justify-center mb-1.5 md:mb-2 shadow-clay-sm
          ${isPositive ? 'bg-emerald-100 text-emerald-500' : 'bg-rose-100 text-rose-500'}
        `}>
          {isPositive ? <Plus className="w-4 h-4 md:w-5 md:h-5" /> : <Minus className="w-4 h-4 md:w-5 md:h-5" />}
        </div>
        <span className="font-bold text-gray-500 text-xs md:text-sm">{t('studentAction.custom.label')}</span>
      </button>
    );
  };

   const renderItemCard = (item: PointItem) => {
     const Icon = iconMap[item.icon] || Star;
     const isPositive = item.points > 0;

     return (
       <button
        key={item.id}
        onClick={() => handleUpdatePoints(item.points, item.name)}
        disabled={loading}
        className={`
          flex flex-col items-center justify-center p-2 md:p-3 rounded-2xl border
          transition-all duration-200 ease-out active:scale-[0.96]
          ${isPositive
            ? 'bg-emerald-50/60 border-emerald-100 hover:border-emerald-300'
            : 'bg-rose-50/60 border-rose-100 hover:border-rose-300'
          }
          hover:shadow-clay hover:-translate-y-0.5
        `}
      >
        <div className={`
          w-9 h-9 md:w-11 md:h-11 rounded-2xl flex items-center justify-center mb-1.5 md:mb-2 shadow-clay-sm
          ${isPositive ? 'bg-white text-emerald-500' : 'bg-white text-rose-500'}
        `}>
          <Icon className="w-4 h-4 md:w-5 md:h-5" />
        </div>
        <span className="font-bold text-gray-800 text-xs md:text-sm mb-1 line-clamp-2 text-center leading-tight min-h-[2em]">{item.name}</span>
        <div className={`
          px-2 py-0.5 rounded-full text-[10px] font-black flex items-center gap-0.5
          ${isPositive ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}
        `}>
          {isPositive ? '+' : ''}{item.points} 🍗
        </div>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal Content */}
      <div className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl h-[85vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col">

        {/* Header Section */}
        <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 text-gray-900 p-4 md:p-5 relative overflow-hidden shrink-0">
           {/* Decorative circles */}
           <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-200/40 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl"></div>
           <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-200/35 rounded-full translate-y-1/2 -translate-x-1/3 blur-2xl"></div>

           <button 
            onClick={onClose}
            className="absolute right-3 top-3 p-1.5 hover:bg-gray-900/5 rounded-full text-gray-500 hover:text-gray-900 transition-colors z-20"
          >
            <X className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {isBatchMode ? (
            <div className="relative z-10 text-center py-4">
               <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-md px-4 py-1.5 rounded-full text-sm font-medium mb-4 border border-white/60 text-indigo-700">
                 <Users className="w-4 h-4" />
                 {t('studentAction.batchMode')}
               </div>
               <h2 className="text-3xl font-black mb-2">
                 {t('studentAction.batchTitle', { count: targetStudents.length })}
               </h2>
            </div>
          ) : (
            <div className="relative z-10 flex flex-row items-center gap-4 md:gap-6">
               {/* Avatar */}
                <div className="relative group shrink-0">
                  <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full bg-white border-2 md:border-[3px] border-indigo-100 shadow-xl overflow-hidden flex items-center justify-center ${!hidePetStatus ? petLifeStyle?.ring || '' : ''}`}>
                     {currentStudent?.petType ? (
                      <CosImage src={getPetImage(currentStudent.petType, level, petConfig)} alt="Pet" className={`w-full h-full object-contain dragon-animate ${!hidePetStatus ? petLifeStyle?.filter || '' : ''}`} />
                     ) : (
                       <span className="text-2xl md:text-4xl">🥚</span>
                     )}
                  </div>
                 <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 text-[10px] md:text-xs font-bold px-1.5 md:px-2 py-0.5 rounded-full border border-yellow-200 shadow-sm whitespace-nowrap z-10">
                   Lv.{level}
                 </div>
               </div>

               {/* Info - Compact Layout */}
               <div className="flex-1 min-w-0">
                 {/* Row 1: Name & Tag */}
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-xl md:text-4xl font-black truncate text-gray-900">
                      {currentStudent?.name}
                    </h2>
                   {currentStudent?.petType && (
                     <span className="text-xs md:text-sm font-normal bg-indigo-100 px-1.5 md:px-2 py-0.5 md:py-1 rounded-md md:rounded-lg border border-indigo-200 text-indigo-700 flex items-center gap-1 shrink-0">
                       <Cat className="w-3 h-3" /> <span className="hidden sm:inline">{currentStudent.nickname || getPetName(currentStudent.petType)}</span><span className="sm:hidden">{currentStudent.nickname || getPetName(currentStudent.petType)}</span>
                     </span>
                    )}
                    {currentStudent?.petType && onChangePet && (
                      <button
                        type="button"
                        onClick={() => onChangePet(currentStudent)}
                        className="text-xs md:text-sm font-medium bg-white/80 hover:bg-white px-2 md:px-2.5 py-0.5 md:py-1 rounded-md md:rounded-lg border border-gray-200 hover:border-indigo-300 text-gray-700 hover:text-indigo-600 transition-colors shrink-0 flex items-center gap-1 whitespace-nowrap"
                      >
                        {t('studentAction.changePet')}
                      </button>
                    )}
                    {petLifeInfo && !hidePetStatus && currentClass?.isAttenuationEnabled !== false && petLifeInfo.lifeState !== 'normal' && (
                      <span className={`text-xs md:text-sm font-bold px-2 py-0.5 rounded-md border shrink-0 ${petLifeStyle?.badge || ''}`}>
                        {getPetLifeStateText(petLifeInfo.lifeState, petLifeConfig)}
                      </span>
                    )}
                  </div>
                  
                  {/* Row 2: Stats & Progress mixed */}
                 <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-gray-600">
                    <div className="flex items-center gap-3 md:gap-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg md:text-3xl font-bold text-gray-900">{currentStudent?.currentPoints}</span>
                        <span className="text-xs md:text-sm opacity-80">{t('studentAction.pointsUnit')}</span>
                      </div>
                      <div className="w-px h-3 md:h-8 bg-gray-300/60"></div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg md:text-3xl font-bold text-amber-600">{currentStudent?.availablePoints ?? 0}</span>
                        <span className="text-xs md:text-sm opacity-80">{t('studentAction.availablePointsUnit')}</span>
                      </div>
                      <div className="w-px h-3 md:h-8 bg-gray-300/60"></div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-lg md:text-3xl font-bold text-gray-900">{currentStudent?.unlockedPets?.length || 0}</span>
                        <span className="text-xs md:text-sm opacity-80">{t('studentAction.petsCountUnit')}</span>
                      </div>
                    </div>

                    {/* Progress Bar - Compact */}
                    <div className="flex-1 min-w-[80px] max-w-[120px] md:max-w-xs">
                        <div className="flex justify-between text-[10px] md:text-xs text-gray-500 mb-0.5">
                          <span>{t('studentAction.currentExp')}</span>
                          <span>{Math.floor(progress)}%</span>
                        </div>
                        <div className="h-1.5 md:h-2 rounded-full overflow-hidden bg-gray-200">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-pink-300 to-rose-400 opacity-90 transition-all duration-500"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                     </div>
                  </div>
                </div>
             </div>
           )}

          {/* Tabs - More compact and closer to top */}
          {!isBatchMode && (
            <div className="flex items-center gap-1 mt-3 md:mt-4 bg-white/70 p-1 rounded-xl backdrop-blur-md w-full md:w-fit md:mx-0 border border-white/60 justify-between md:justify-start">
              <button 
                onClick={() => setActiveTab('action')}
                className={`flex-1 md:flex-none justify-center px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-bold flex items-center gap-1.5 md:gap-2 transition-all ${activeTab === 'action' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:bg-white/60'}`}
              >
                <Zap className="w-3.5 h-3.5 md:w-4 md:h-4" /> {t('studentAction.tabs.action')}
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 md:flex-none justify-center px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-bold flex items-center gap-1.5 md:gap-2 transition-all ${activeTab === 'history' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:bg-white/60'}`}
              >
                <History className="w-3.5 h-3.5 md:w-4 md:h-4" /> {t('studentAction.tabs.history')}
              </button>
              <button
                onClick={() => setActiveTab('pets')}
                className={`flex-1 md:flex-none justify-center px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-bold flex items-center gap-1.5 md:gap-2 transition-all ${activeTab === 'pets' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-600 hover:bg-white/60'}`}
              >
                <Cat className="w-3.5 h-3.5 md:w-4 md:h-4" /> {t('studentAction.tabs.pets')}
              </button>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 pt-3 custom-scrollbar bg-white">
          
          {/* TAB: ACTIONS */}
          {activeTab === 'action' && (
            <>
              {/* Sub-tabs: Checkin / Positive / Negative */}
              <div className="flex gap-2 mb-4">
                {!isBatchMode && (
                  <button
                    onClick={() => setActionSubTab('checkin')}
                    className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${
                      actionSubTab === 'checkin'
                        ? 'bg-sky-100 text-sky-700 shadow-sm'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    <Check className="w-4 h-4" />
                    {t('studentAction.checkinItems')}
                    {checkinTasks.length > 0 && <span className="text-xs opacity-70">({checkinTasks.length})</span>}
                  </button>
                )}
                <button
                  onClick={() => setActionSubTab('behavior')}
                  className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1.5 ${
                    actionSubTab === 'behavior'
                      ? 'bg-amber-100 text-amber-700 shadow-sm'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  <Sparkles className="w-4 h-4" />
                  {t('studentAction.behaviorItems')}
                  {(positiveItems.length + negativeItems.length) > 0 && <span className="text-xs opacity-70">({positiveItems.length + negativeItems.length})</span>}
                </button>
              </div>

              {actionSubTab === 'checkin' && !isBatchMode && currentStudent && (
                <div className="animate-in fade-in duration-200">
                  {checkinTasks.length > 0 ? (
                    <div className="flex flex-col gap-2">
                      {checkinTasks.map(task => {
                        const completed = checkinRecords.some(r => r.taskId === task.id && r.studentId === currentStudent.id);
                        const isOp = checkinOperatingId === task.id;
                        const TaskIcon = (task.icon && iconMap[task.icon]) || Sparkles;
                        return (
                          <button
                            key={task.id}
                            onClick={() => handleToggleCheckin(task.id)}
                            disabled={isOp || checkinOperatingId !== null}
                            className={`
                              group flex items-center gap-2.5 md:gap-3 px-3 py-2 md:py-2.5 rounded-xl bg-white border shadow-sm
                              transition-all text-left w-full hover:shadow-md
                              ${completed ? 'border-emerald-100' : 'border-gray-100'}
                              ${isOp ? 'opacity-50' : ''}
                            `}
                          >
                            <div className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                              <TaskIcon className="w-4 h-4 md:w-5 md:h-5 text-amber-500" />
                            </div>
                            <div className="flex-1 min-w-0 flex items-center gap-1.5">
                              <span className={`font-bold text-sm md:text-base truncate ${completed ? 'text-emerald-700' : 'text-gray-800'}`}>
                                {task.name}
                              </span>
                              <span className={`
                                px-1.5 py-0.5 rounded-full text-[10px] font-bold shrink-0
                                ${completed ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}
                              `}>
                                +{task.points}
                              </span>
                            </div>
                            <div className={`
                              w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center shrink-0 transition-all
                              ${completed
                                ? 'bg-emerald-500 text-white shadow-sm'
                                : 'border-2 border-gray-300 text-gray-300 group-hover:border-emerald-400 group-hover:text-emerald-400'
                              }
                            `}>
                              <Check className="w-3.5 h-3.5 md:w-4 md:h-4" strokeWidth={3} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center min-h-[200px] text-center text-gray-400">
                      <Check className="w-12 h-12 mb-3 opacity-20" />
                      <p>{t('studentAction.noCheckinTasks')}</p>
                    </div>
                  )}
                </div>
              )}

              {actionSubTab === 'behavior' && (
                <div className="animate-in fade-in duration-200">
                  {/* Filter tags */}
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <button
                      onClick={() => setBehaviorFilter('all')}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border ${
                        behaviorFilter === 'all'
                          ? 'bg-amber-100 border-amber-300 text-amber-700 shadow-sm'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-amber-200 hover:text-amber-600'
                      }`}
                    >
                      {t('studentAction.behaviorFilterAll')}
                      <span className="ml-1 opacity-70">({positiveItems.length + negativeItems.length})</span>
                    </button>
                    <button
                      onClick={() => setBehaviorFilter('positive')}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border flex items-center gap-1 ${
                        behaviorFilter === 'positive'
                          ? 'bg-emerald-100 border-emerald-300 text-emerald-700 shadow-sm'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-emerald-200 hover:text-emerald-600'
                      }`}
                    >
                      <Plus className="w-3 h-3" />
                      {t('studentAction.behaviorFilterPositive')}
                      <span className="ml-0.5 opacity-70">({positiveItems.length})</span>
                    </button>
                    <button
                      onClick={() => setBehaviorFilter('negative')}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all border flex items-center gap-1 ${
                        behaviorFilter === 'negative'
                          ? 'bg-rose-100 border-rose-300 text-rose-700 shadow-sm'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-rose-200 hover:text-rose-600'
                      }`}
                    >
                      <Minus className="w-3 h-3" />
                      {t('studentAction.behaviorFilterNegative')}
                      <span className="ml-0.5 opacity-70">({negativeItems.length})</span>
                    </button>
                  </div>

                  {/* Positive section */}
                  {(behaviorFilter === 'all' || behaviorFilter === 'positive') && (
                    <div className={behaviorFilter === 'all' ? 'mb-4' : ''}>
                      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2 md:gap-3">
                        {positiveItems.map(renderItemCard)}
                        {renderCustomCard('positive')}
                      </div>
                    </div>
                  )}

                  {/* Negative section — always starts on a new row */}
                  {(behaviorFilter === 'all' || behaviorFilter === 'negative') && (
                    <div>
                      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2 md:gap-3">
                        {negativeItems.map(renderItemCard)}
                        {renderCustomCard('negative')}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* TAB: HISTORY */}
          {activeTab === 'history' && (
            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              <div className="flex justify-end mb-2">
                {history.length > 0 && (
                  <button
                    onClick={handleExportHistory}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    {t('studentAction.export')}
                  </button>
                )}
              </div>

              {Object.keys(groupedHistory).length === 0 ? (
                <div className="text-center py-20 text-gray-400">
                  <Clock className="w-16 h-16 mx-auto mb-4 opacity-20" />
                  <p>{t('studentAction.noHistory')}</p>
                </div>
              ) : (
                Object.entries(groupedHistory).map(([date, records]) => (
                  <div key={date}>
                    <div className="flex items-center gap-4 mb-4">
                       <div className="h-px bg-gray-100 flex-1"></div>
                       <span className="text-xs font-bold text-gray-400 bg-gray-50 px-3 py-1 rounded-full">{date}</span>
                       <div className="h-px bg-gray-100 flex-1"></div>
                    </div>

                    <div className="space-y-3">
                      {records.map((record) => {
                        const isPrestige = record.amount === 0;
                        const isUndoRecord = record.revertedFromId != null;
                        const isReverted = record.revertedAt != null;
                        const canUndo = !isPrestige && !isUndoRecord && !isReverted && record.amount !== 0;
                        
                        return (
                          <div
                            key={record.id}
                            className={`bg-white border border-gray-100 rounded-2xl p-4 flex items-center gap-4 shadow-sm relative ${isReverted ? 'opacity-60' : ''}`}
                          >
                            <span className="text-gray-400 font-mono text-sm">
                              {new Date(record.createdAt).toLocaleTimeString(activeLanguage, { hour: '2-digit', minute: '2-digit', hour12: false })}
                            </span>

                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                              isPrestige
                                ? 'bg-yellow-100 text-yellow-500'
                                : isUndoRecord
                                  ? 'bg-indigo-100 text-indigo-500'
                                  : record.amount >= 0 ? 'bg-green-100 text-green-500' : 'bg-gray-100 text-gray-500'
                            }`}>
                               {isPrestige ? <Award className="w-4 h-4" /> : (isUndoRecord ? <RotateCcw className="w-4 h-4" /> : (record.amount >= 0 ? <CheckCircle2 className="w-4 h-4" /> : <RotateCcw className="w-4 h-4" />))}
                            </div>

                            <div className="flex-1">
                              <span className={`text-sm ${isPrestige ? 'text-yellow-600 font-bold' : 'text-gray-800 font-bold'}`}>
                                {isPrestige ? t('common.prestigeReason') : (record.reason || t('studentAction.unknownAction'))}
                                {isReverted && <span className="ml-2 text-xs font-bold text-gray-400">{t('studentAction.reverted')}</span>}
                              </span>
                            </div>

                            {!isPrestige && (
                              <div className="flex items-center gap-3">
                                <div className={`font-black text-base ${record.amount >= 0 ? 'text-green-500' : 'text-gray-400'}`}>
                                  {record.amount > 0 ? '+' + record.amount : record.amount}
                                  <span className="text-sm ml-1">🐟</span>
                                </div>
                                {canUndo && (
                                  <button
                                    onClick={() => setUndoTarget(record)}
                                    className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-bold hover:bg-gray-200 transition-colors"
                                  >
                                    {t('studentAction.undo')}
                                  </button>
                                )}
                              </div>
                            )}

                            {isReverted && (
                              <div className="pointer-events-none absolute left-4 right-4 top-1/2 -translate-y-1/2 border-t border-gray-300"></div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* TAB: CALENDAR */}
          {activeTab === 'calendar' && !isBatchMode && currentStudent && (
            <div className="animate-in fade-in duration-200">
              {(() => {
                const cells = buildCalendarCells(calendarMonth);
                // Aggregate per-day stats so the JSX below can stay simple.
                const dayStats: Record<string, { count: number; points: number; tasks: { name: string; points: number }[] }> = {};
                for (const r of calendarRecords) {
                  const key = normalizeDateString(r.date);
                  if (!key) continue;
                  if (!dayStats[key]) dayStats[key] = { count: 0, points: 0, tasks: [] };
                  dayStats[key].count += 1;
                  dayStats[key].points += r.pointsAwarded || 0;
                  dayStats[key].tasks.push({
                    name: r.task?.name || '?',
                    points: r.pointsAwarded || 0,
                  });
                }
                const todayKey = formatDateKey(new Date());
                const totalCount = calendarRecords.length;
                const totalPoints = calendarRecords.reduce(
                  (sum, r) => sum + (r.pointsAwarded || 0),
                  0,
                );
                const monthLabel = new Intl.DateTimeFormat(activeLanguage, {
                  year: 'numeric',
                  month: 'long',
                }).format(calendarMonth);
                const goPrev = () => {
                  setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
                  setCalendarSelectedDay(null);
                };
                const goNext = () => {
                  setCalendarMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
                  setCalendarSelectedDay(null);
                };
                const goToday = () => {
                  const now = new Date();
                  setCalendarMonth(new Date(now.getFullYear(), now.getMonth(), 1));
                  setCalendarSelectedDay(null);
                };

                return (
                  <>
                    {/* Header: month nav + summary */}
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={goPrev}
                          className="w-8 h-8 rounded-lg bg-gray-50 hover:bg-sky-50 hover:text-sky-500 text-gray-500 flex items-center justify-center transition-colors"
                          aria-label="prev month"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <div className="font-bold text-gray-800 min-w-[110px] text-center">
                          {monthLabel}
                        </div>
                        <button
                          onClick={goNext}
                          className="w-8 h-8 rounded-lg bg-gray-50 hover:bg-sky-50 hover:text-sky-500 text-gray-500 flex items-center justify-center transition-colors"
                          aria-label="next month"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                        <button
                          onClick={goToday}
                          className="ml-1 px-2.5 py-1 rounded-lg bg-sky-50 text-sky-600 text-xs font-bold hover:bg-sky-100 transition-colors"
                        >
                          {t('studentAction.calendar.today')}
                        </button>
                      </div>
                      <div className="text-xs text-gray-500">
                        {t('studentAction.calendar.summary', {
                          count: totalCount,
                          points: totalPoints,
                        })}
                      </div>
                    </div>

                    {/* Weekday header */}
                    <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                      {(['mon','tue','wed','thu','fri','sat','sun'] as const).map(k => (
                        <div key={k} className="text-center text-[11px] font-bold text-gray-400 py-1">
                          {t(`checkin.weekdayShort.${k}`)}
                        </div>
                      ))}
                    </div>

                    {/* Date grid */}
                    <div className="grid grid-cols-7 gap-1.5">
                      {cells.map((cell, idx) => {
                        const key = formatDateKey(cell.date);
                        const stats = dayStats[key];
                        const hasCheckin = !!stats && stats.count > 0;
                        const isToday = key === todayKey;
                        const isSelected = calendarSelectedDay === key && cell.inMonth;
                        const visibleTasks = stats?.tasks.slice(0, 3) ?? [];
                        const extraTasks = stats ? stats.tasks.length - visibleTasks.length : 0;
                        return (
                          <div
                            key={idx}
                            onClick={() => {
                              if (cell.inMonth && hasCheckin) {
                                setCalendarSelectedDay(prev => prev === key ? null : key);
                              }
                            }}
                            className={`relative aspect-square rounded-lg border p-1 sm:p-1.5 flex flex-col overflow-hidden transition-all ${
                              !cell.inMonth
                                ? 'bg-gray-50/50 border-gray-100 text-gray-300'
                                : hasCheckin
                                  ? 'bg-sky-50 border-sky-200 text-sky-700'
                                  : 'bg-white border-gray-100 text-gray-600'
                            } ${isToday && cell.inMonth ? 'ring-2 ring-pink-300 ring-offset-1' : ''} ${isSelected ? 'ring-2 ring-sky-400 ring-offset-1' : ''} ${cell.inMonth && hasCheckin ? 'cursor-pointer active:scale-95' : ''}`}
                          >
                            <span className={`text-[11px] sm:text-xs font-bold leading-none ${hasCheckin && cell.inMonth ? 'text-sky-700' : ''}`}>
                              {cell.date.getDate()}
                            </span>
                            {cell.inMonth && hasCheckin && (
                              <>
                                {/* Mobile: dots + count */}
                                <div className="flex-1 mt-1 flex items-center justify-center sm:hidden">
                                  <div className="flex flex-col items-center gap-0.5">
                                    <div className="flex gap-0.5 flex-wrap justify-center">
                                      {stats!.tasks.slice(0, 4).map((_, i) => (
                                        <div key={i} className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                                      ))}
                                    </div>
                                    {stats!.count > 0 && (
                                      <span className="text-[10px] font-bold text-sky-600 leading-none">
                                        +{stats!.points}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {/* Desktop: task names */}
                                <div className="hidden sm:flex flex-1 mt-1 flex-col gap-0.5 overflow-hidden">
                                  {visibleTasks.map((task, i) => (
                                    <div
                                      key={i}
                                      className="text-[10px] leading-tight font-bold text-sky-700 bg-white/70 rounded px-1 py-0.5 truncate"
                                      title={`${task.name} +${task.points}`}
                                    >
                                      {task.name}
                                    </div>
                                  ))}
                                  {extraTasks > 0 && (
                                    <div className="text-[10px] leading-tight text-sky-500 font-bold text-center">
                                      +{extraTasks}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Selected day detail popover (mobile-friendly) */}
                    {calendarSelectedDay && dayStats[calendarSelectedDay] && (
                      <div className="mt-2 bg-sky-50 border border-sky-200 rounded-xl p-3 animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-bold text-sky-700">{calendarSelectedDay}</span>
                          <button
                            onClick={() => setCalendarSelectedDay(null)}
                            className="p-0.5 hover:bg-sky-100 rounded-full transition-colors"
                          >
                            <X className="w-3.5 h-3.5 text-sky-400" />
                          </button>
                        </div>
                        <div className="space-y-1">
                          {dayStats[calendarSelectedDay].tasks.map((task, i) => (
                            <div key={i} className="flex items-center justify-between bg-white rounded-lg px-2.5 py-1.5">
                              <span className="text-sm text-gray-700">{task.name}</span>
                              <span className="text-xs font-bold text-sky-600">+{task.points}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Loading / empty hint */}
                    {calendarLoading && (
                      <div className="text-center text-xs text-gray-400 mt-3">
                        {t('common.loading')}
                      </div>
                    )}
                    {!calendarLoading && totalCount === 0 && (
                      <div className="text-center text-xs text-gray-400 mt-4">
                        {t('studentAction.calendar.empty')}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* TAB: PETS */}
          {activeTab === 'pets' && (
             <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4 animate-in slide-in-from-right-4 duration-300">
               {petConfig.map(config => {
                 const isUnlocked = currentStudent?.unlockedPets?.includes(config.id);
                 const isCurrent = currentStudent?.petType === config.id;
                 
                 return (
                   <div key={config.id} className={`relative rounded-xl p-3 border-2 flex flex-col items-center transition-all ${isUnlocked ? 'bg-white border-indigo-100' : 'bg-gray-50 border-gray-100 opacity-60 grayscale'} ${isCurrent ? 'ring-2 ring-indigo-500 ring-offset-2 border-indigo-500' : ''}`}>
                      {isUnlocked && (
                        <div className="absolute top-0 right-0 z-20 bg-gradient-to-l from-yellow-400 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg rounded-tr-lg shadow-sm flex items-center gap-0.5">
                          <Award className="w-3 h-3" />
                        </div>
                      )}
                      {isCurrent && (
                        <div className="absolute top-2 left-2 text-indigo-500 z-30">
                          <CheckCircle className="w-4 h-4 fill-current" />
                        </div>
                      )}
                      <div className="w-16 h-16 mb-2">
                        <CosImage src={getPreviewImage(config, true)} alt={getPetName(config.id)} className="w-full h-full object-contain" />
                      </div>
                      <span className="text-sm font-bold text-gray-700">{getPetName(config.id)}</span>
                      {!isUnlocked && <span className="text-xs text-gray-400 mt-1">{t('studentAction.locked')}</span>}
                   </div>
                 );
               })}
             </div>
          )}

        </div>

        {undoTarget && !isBatchMode && (
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6 z-50">
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-5">
              <div className="text-lg font-black text-gray-800">{t('studentAction.confirmUndo.title')}</div>
              <div className="mt-2 text-sm text-gray-500 leading-relaxed">
                {t('studentAction.confirmUndo.desc', { action: undoTarget.amount > 0 ? t('studentAction.confirmUndo.add') : t('studentAction.confirmUndo.deduct') })}
              </div>
              <div className="mt-4 flex gap-3">
                <button
                  onClick={() => setUndoTarget(null)}
                  disabled={undoSubmitting}
                  className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-600 font-bold hover:bg-gray-200 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={submitUndo}
                  disabled={undoSubmitting}
                  className="flex-1 py-2.5 rounded-xl bg-red-50 text-red-600 font-bold hover:bg-red-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {undoSubmitting ? t('common.loading') : t('studentAction.confirmUndo.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}
      {/* Custom Score Modal */}
      {customScoreOpen && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setCustomScoreOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setCustomScoreOpen(false)}
              className="absolute right-3 top-3 p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${customScoreType === 'positive' ? 'text-emerald-600' : 'text-rose-600'}`}>
              {customScoreType === 'positive' ? <Plus className="w-5 h-5" /> : <Minus className="w-5 h-5" />}
              {customScoreType === 'positive' ? t('studentAction.custom.titlePositive') : t('studentAction.custom.titleNegative')}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-bold text-gray-600 mb-1">{t('studentAction.custom.points')}</label>
                <input
                  ref={customScoreInputRef}
                  type="number"
                  min="1"
                  max="9999"
                  value={customScoreValue}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || (parseInt(val, 10) >= 0 && parseInt(val, 10) <= 9999)) {
                      setCustomScoreValue(val);
                    }
                  }}
                  placeholder={t('studentAction.custom.pointsPlaceholder')}
                  className="w-full border-2 border-gray-200 rounded-xl px-4 py-2.5 text-lg font-bold text-center focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
                  onKeyDown={(e) => e.key === 'Enter' && handleCustomScoreSubmit()}
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-600 mb-1">
                  {t('studentAction.custom.reason')}
                  <span className="font-normal text-gray-400 ml-1">{t('studentAction.custom.optional')}</span>
                </label>
                <input
                  type="text"
                  value={customScoreReason}
                  onChange={(e) => setCustomScoreReason(e.target.value)}
                  placeholder={customScoreType === 'positive' ? t('studentAction.custom.defaultPositive') : t('studentAction.custom.defaultNegative')}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2 text-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
                  onKeyDown={(e) => e.key === 'Enter' && handleCustomScoreSubmit()}
                />
              </div>
              <button
                onClick={handleCustomScoreSubmit}
                disabled={!customScoreValue || parseInt(customScoreValue, 10) <= 0 || parseInt(customScoreValue, 10) > 9999}
                className={`w-full py-2.5 rounded-xl font-bold text-white transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed ${
                  customScoreType === 'positive'
                    ? 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-200'
                    : 'bg-rose-500 hover:bg-rose-600 shadow-rose-200'
                }`}
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// Helper component for icon
function CheckCircle({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
    </svg>
  );
}
